// Auto-join: the River starts playing as soon as the page opens. Browsers only allow sound after
// the visitor has interacted with the page (unless they allow autoplay for this site), so if the
// audio context stays suspended the player waits, and the first tap, click or key press anywhere
// on the page starts the stream.
import { engineHost } from "../audio/host";
import type { RiverPlayer } from "./player";

/** Did the visitor press "Leave Stream" during this visit? Then don't start again by itself. */
let left = false;
export function markLeft(): void {
  left = true;
}

const GESTURES = ["pointerdown", "keydown", "touchend", "click"] as const;

/** Start the stream now if the browser allows it, else on the first gesture. Returns a cleanup. */
export function autoJoin(player: RiverPlayer): () => void {
  if (left || player.status !== "idle") return () => undefined;
  let done = false;
  const finish = () => {
    done = true;
    for (const g of GESTURES) window.removeEventListener(g, onGesture, true);
  };
  const tryStart = (allowWait: boolean) => {
    // engineHost() resumes the context synchronously, inside the gesture (Safari needs that).
    void engineHost()
      .then(async (host) => {
        if (done) return;
        if (host.ctx.state !== "running")
          await Promise.race([host.ctx.resume(), new Promise((r) => setTimeout(r, allowWait ? 400 : 1000))]);
        if (done) return;
        if ((host.ctx.state as AudioContextState) === "running") {
          finish();
          if (player.status === "idle" || player.status === "waiting") await player.join(host);
        } else if (allowWait) {
          player.setWaiting(true);
        }
      })
      .catch(() => undefined);
  };
  function onGesture(e: Event) {
    if (done) return;
    if (e.type === "keydown" && ["Tab", "Shift", "Control", "Alt", "Meta", "Escape"].includes((e as KeyboardEvent).key))
      return;
    if (player.status !== "waiting" && player.status !== "idle") return finish();
    tryStart(false);
  }
  for (const g of GESTURES) window.addEventListener(g, onGesture, true);
  tryStart(true);
  return () => {
    finish();
    player.setWaiting(false);
  };
}
